<template>
  <section :class="$style.page">
    <img
      src="@/assets/images/logo.svg?url"
      :class="[$style.page__logo, $style.logo]"
    />
    <div :class="[$style.page__form, $style.form]">
      <div :class="[$style.form__loading, $style.loading]" v-if="isLoading">
        <LoadingIndicator :class="$style.loading__icon" />
      </div>
      <LoginPageForm
        @submit="onLoginFormSubmit"
        @change="onChangeFormInput"
        :invalid="isFormInvalid"
        :isLoading="isLoading"
      />
    </div>
    <footer :class="$style.page__footer">
      <RouterLink :to="{ name: 'resetPassword' }">
        パスワードをお忘れの方はこちら
      </RouterLink>
    </footer>
  </section>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import LoginPageForm from './LoginPageForm.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { decodeLoginRedirectionLocation } from '@/router'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { useRoute, useRouter } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import { useStore } from '@/store'
import { captureException } from '@sentry/vue'

const isFormInvalid = ref(false)
const isLoading = ref(false)

const route = useRoute('login')
const redirectLocation = computed(() =>
  decodeLoginRedirectionLocation(route.query)
)

const alert = useAlert()

const ROOT_PATH = '/'
onMounted(() => {
  if (redirectLocation.value.path !== ROOT_PATH) {
    alert.warning('セッションの有効期限が切れました。ログインしてください。')
  }
})

const onChangeFormInput = () => {
  isFormInvalid.value = false
}

const store = useStore()
const router = useRouter()
const onLoginFormSubmit = async (formValues: {
  email: string
  password: string
}): Promise<void> => {
  isLoading.value = true
  try {
    await store.dispatch('login', formValues)
    alert.clear()
    // eslint-disable-next-line fp/no-mutating-methods
    await router.push(redirectLocation.value)
  } catch (error) {
    if (isClientError(error)) {
      alert.error('メールアドレスかパスワードが間違っています。')
      isFormInvalid.value = true
      return
    }

    captureException(error)
    alert.error('申し訳ございませんが、エラーです。')
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" module>
.page {
  width: 100%;
  max-width: 480px;
  padding: 0 16px;

  &__form {
    padding: 40px;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-v2-base;

    &:nth-child(n + 2) {
      margin-top: 36px;
    }
  }

  &__footer {
    font-size: 14px;
    text-align: center;

    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }
}

.logo {
  display: block;
  width: 80%;
  min-width: 240px;
  max-width: 320px;
  margin-right: auto;
  margin-left: auto;
}

.form {
  position: relative;

  &__loading {
    position: absolute;
    z-index: 1;
    inset: 0;
  }
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  background-color: $color-surface-primary;
}
</style>
